import React, { useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import type { DashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import type { SameDayCancellationOverviewResult } from '@services/api/dashboard/scheduling.api'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  mapSameDayCancellationChartToOverviewLineChart,
  mapSchedulingOverviewToSameDayCancellationMetrics,
} from '@utils/overviewAppointments.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'
import { getOverviewSameDayCancellationLineChart } from '../dashboardOverview.mockData'
import { getOverviewSameDayCancellationMetrics } from '../overviewMetrics.mockData'

type SameDayCancellationCardProps = {
  range: RangeKey
  schedulingOverview: DashboardUtilizationData['schedulingOverview']
  schedulingLoading: boolean
  sameDayCancellationDetail: SameDayCancellationOverviewResult | null
  loading: boolean
}

export function SameDayCancellationCard({
  range,
  schedulingOverview,
  schedulingLoading,
  sameDayCancellationDetail,
  loading,
}: SameDayCancellationCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const lineChart = useMemo(() => {
    if (useMocks) return getOverviewSameDayCancellationLineChart(range)
    return mapSameDayCancellationChartToOverviewLineChart(
      sameDayCancellationDetail?.chart,
    )
  }, [useMocks, range, sameDayCancellationDetail?.chart])
  const metrics = useMemo(() => {
    const mockMetrics = withDashboardMetricShellValues(
      getOverviewSameDayCancellationMetrics(range),
      useMocks,
    )
    if (!schedulingOverview) return mockMetrics
    return mapSchedulingOverviewToSameDayCancellationMetrics(
      schedulingOverview,
      mockMetrics,
    )
  }, [schedulingOverview, range, useMocks])
  // Total appointments and open-chair minutes are capacity/denominators, not
  // cancellation activity; only counts and chart values establish data.
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading || schedulingLoading,
    hasNonZeroDashboardValues(
      sameDayCancellationDetail?.sameDayCancellations,
      schedulingOverview?.summary.sameDayCancellations.current,
      ...(lineChart?.data.map((point) => point.value) ?? []),
    ),
  )
  const detailSettled = sameDayCancellationDetail != null || !loading
  const schedulingSettled = schedulingOverview != null || !schedulingLoading
  const hasPayload = detailSettled && schedulingSettled
  const pending = isDashboardSectionPending(
    loading || schedulingLoading,
    hasPayload,
  )

  return (
    <DashboardSectionCard
      title="Same-day cancellation"
      titleHref="/dashboard/same-day-cancellation"
      className="dashboard-overview-line-chart-card"
      loading={pending}
      skeleton={{ metrics: 3, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-line-chart-content">
        <div className="dashboard-overview-acceptance-metrics-row">
          {metrics.map((metric) => (
            <DashboardMetricItem
              key={metric.id ?? metric.leftLabel}
              {...metricDataToItemPropsWithLayoutClass(
                metric,
                'dashboard-overview-acceptance-metric',
              )}
            />
          ))}
        </div>
        {lineChart ? (
          <div className="dashboard-overview-line-chart-wrap">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={lineChart.data}
                margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="0"
                  vertical={false}
                  horizontal
                  stroke="var(--gray-200)"
                />
                <XAxis
                  type="category"
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 14, fill: 'var(--gray-400)' }}
                  tickMargin={8}
                />
                <YAxis
                  type="number"
                  domain={lineChart.yDomain}
                  ticks={lineChart.yTicks}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 14, fill: 'var(--gray-400)' }}
                  tickFormatter={(value: number) =>
                    value === 0 ? '0' : `${value} ${value === 1 ? 'hr' : 'hrs'}`
                  }
                  width={48}
                />
                <Tooltip
                  formatter={(value: number) => [`${value} hrs`, '']}
                  contentStyle={{ fontSize: 11, borderRadius: 6 }}
                  labelFormatter={(label) => String(label)}
                />
                <Line
                  type="linear"
                  dataKey="value"
                  stroke="var(--orange-400)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </div>
    </DashboardSectionCard>
  )
}
